import Papa from 'papaparse';
import { Building, RetrofitPotential, CostBenefitAnalysis, PropertyManager } from '../types/building';
import { LL97Service } from './ll97Service';
import { RetrofitService } from './retrofitService';

export interface NYCBuildingDataRow {
    'Property Name': string;
    'Address'?: string;  // Sample data format
    'Address 1'?: string;  // Real NYC data format
    'Borough'?: string;  // Sample data format
    'City'?: string;  // Real NYC data format
    'Postal Code': string;
    'Primary Property Type - Self Selected': string;
    'Largest Property Use Type': string;
    'DOF Gross Floor Area (ft²)'?: string;  // Sample data format
    'Largest Property Use Type - Gross Floor Area (ft²)'?: string;  // Real NYC data format
    'ENERGY STAR Score': string;
    'Site EUI (kBtu/ft²)': string;
    'Source EUI (kBtu/ft²)': string;
    'Weather Normalized Site EUI (kBtu/ft²)': string;
    'Weather Normalized Source EUI (kBtu/ft²)': string;
    'Total GHG Emissions (Metric Tons CO2e)'?: string;  // Sample data format
    'Total (Location-Based) GHG Emissions (Metric Tons CO2e)'?: string;  // Real NYC data format
    'Direct GHG Emissions (Metric Tons CO2e)'?: string;  // Sample data format
    'Indirect GHG Emissions (Metric Tons CO2e)'?: string;  // Sample data format
    'Indirect (Location-Based) GHG Emissions (Metric Tons CO2e)'?: string;  // Real NYC data format
    'Electricity Use - Grid Purchase (kWh)': string;
    'Natural Gas Use (kBtu)': string;
}

export class BuildingDataService {
    
    static async parseCSVFile(file: File): Promise<Building[]> {
        return new Promise((resolve, reject) => {
            Papa.parse(file, {
                header: true,
                skipEmptyLines: true,
                complete: (results) => {
                    try {
                        console.log('Papa parse complete. Rows found:', results.data.length);
                        console.log('Sample row:', results.data[0]);
                        console.log('Parse errors:', results.errors);
                        
                        const buildings = this.transformDataToBuildings(results.data as NYCBuildingDataRow[]);
                        console.log('Transformed buildings:', buildings.length);
                        resolve(buildings);
                    } catch (error) {
                        console.error('Error in transformDataToBuildings:', error);
                        reject(error);
                    }
                },
                error: (error) => {
                    console.error('Papa parse error:', error);
                    reject(error);
                }
            });
        });
    }
    
    static transformDataToBuildings(rows: NYCBuildingDataRow[]): Building[] {
        console.log('Starting transformation of', rows.length, 'rows');
        const validRows = rows.filter(row => this.isValidRow(row));
        console.log('Valid rows after filtering:', validRows.length);
        
        const buildings = validRows
            .map((row, index) => {
                try {
                    return this.transformRowToBuilding(row, index);
                } catch (error) {
                    console.error('Error transforming row', index, ':', error, row);
                    return null;
                }
            })
            .filter(building => building !== null) as Building[];
            
        console.log('Final transformed buildings:', buildings.length);
        return buildings;
    }
    
    static isValidRow(row: NYCBuildingDataRow): boolean {
        const grossFloorArea = row['DOF Gross Floor Area (ft²)'] || row['Largest Property Use Type - Gross Floor Area (ft²)'];
        const totalGHGEmissions = row['Total GHG Emissions (Metric Tons CO2e)'] || row['Total (Location-Based) GHG Emissions (Metric Tons CO2e)'];
        
        const isValid = !!(
            row['Property Name'] &&
            grossFloorArea &&
            totalGHGEmissions &&
            parseFloat(grossFloorArea) > 0
        );
        
        if (!isValid) {
            console.log('Invalid row:', {
                propertyName: !!row['Property Name'],
                grossFloorArea: !!grossFloorArea,
                totalGHGEmissions: !!totalGHGEmissions,
                grossFloorAreaValue: grossFloorArea,
                totalGHGEmissionsValue: totalGHGEmissions
            });
        }
        
        return isValid;
    }
    
    static transformRowToBuilding(row: NYCBuildingDataRow, index: number): Building | null {
        try {
            // Handle different data formats with proper fallbacks
            const grossFloorArea = parseFloat(row['DOF Gross Floor Area (ft²)'] || row['Largest Property Use Type - Gross Floor Area (ft²)'] || '0') || 0;
            const totalGHGEmissions = parseFloat(row['Total GHG Emissions (Metric Tons CO2e)'] || row['Total (Location-Based) GHG Emissions (Metric Tons CO2e)'] || '0') || 0;
            const directGHGEmissions = parseFloat(row['Direct GHG Emissions (Metric Tons CO2e)'] || '0') || 0;
            const indirectGHGEmissions = parseFloat(row['Indirect GHG Emissions (Metric Tons CO2e)'] || row['Indirect (Location-Based) GHG Emissions (Metric Tons CO2e)'] || '0') || 0;
            
            const buildingType = row['Primary Property Type - Self Selected'] || row['Largest Property Use Type'] || 'Mixed Use';
            const address = row['Address'] || row['Address 1'] || '';
            const borough = row['Borough'] || row['City'] || '';
            
            // Calculate LL97 fines
            const ll97Current = LL97Service.calculateLL97Fine(buildingType, grossFloorArea, totalGHGEmissions, 2025);
            const ll97Future = LL97Service.calculateLL97Fine(buildingType, grossFloorArea, totalGHGEmissions, 2030);
            
            // Generate retrofit packages
            const building: Partial<Building> = {
                id: `building-${index}`,
                propertyName: row['Property Name'] || '',
                address,
                borough,
                zipCode: row['Postal Code'] || '',
                buildingType,
                primaryPropertyType: buildingType,
                grossFloorArea,
                energyStarScore: parseFloat(row['ENERGY STAR Score'] || '0') || undefined,
                siteEUI: parseFloat(row['Site EUI (kBtu/ft²)'] || '0') || 0,
                sourceEUI: parseFloat(row['Source EUI (kBtu/ft²)'] || '0') || 0,
                weatherNormalizedSiteEUI: parseFloat(row['Weather Normalized Site EUI (kBtu/ft²)'] || '0') || 0,
                weatherNormalizedSourceEUI: parseFloat(row['Weather Normalized Source EUI (kBtu/ft²)'] || '0') || 0,
                totalGHGEmissions,
                directGHGEmissions,
                indirectGHGEmissions,
                electricityUse: parseFloat(row['Electricity Use - Grid Purchase (kWh)'] || '0') || 0,
                naturalGasUse: parseFloat(row['Natural Gas Use (kBtu)'] || '0') || 0,
                currentLL97Fee: ll97Current.currentAnnualFine,
                projectedLL97Fee2030: ll97Future.projected2030Fine,
                propertyManager: this.generatePropertyManagerData(index, buildingType)
            };
            
            // Generate retrofit analysis
            const retrofitPackages = RetrofitService.generateRetrofitPackages(building as Building);
            const bestPackage = retrofitPackages[0]; // Highest ll97SavingsVsCost ratio
            
            if (bestPackage) {
                const viability = RetrofitService.calculateRetrofitViability(building as Building, bestPackage);
                
                building.retrofitPotential = {
                    estimatedEnergyReduction: bestPackage.totalEnergyReduction,
                    estimatedGHGReduction: bestPackage.totalGHGReduction,
                    estimatedCostSavings: ll97Current.cumulativeFines10Year * (bestPackage.totalGHGReduction / 100),
                    retrofitCost: bestPackage.totalCost,
                    paybackPeriod: bestPackage.combinedPaybackPeriod
                };
                
                building.costBenefitAnalysis = {
                    netPresentValue: bestPackage.netPresentValue,
                    internalRateOfReturn: this.calculateIRR(bestPackage.totalCost, building.retrofitPotential.estimatedCostSavings, 10),
                    totalSavingsOverLifetime: ll97Current.cumulativeFines10Year - bestPackage.totalCost,
                    isViableProspect: viability.isViable,
                    confidenceScore: viability.confidenceScore
                };
            }
            
            return building as Building;
        } catch (error) {
            console.warn(`Error processing building data for row ${index}:`, error);
            return null;
        }
    }
    
    static generatePropertyManagerData(index: number, buildingType: string) {
        const firstNames = ['Michael', 'Sarah', 'David', 'Jennifer', 'Robert', 'Lisa', 'James', 'Maria', 'John', 'Emily', 'Carlos', 'Anna', 'Thomas', 'Rebecca', 'Daniel'];
        const lastNames = ['Johnson', 'Williams', 'Brown', 'Jones', 'Garcia', 'Miller', 'Davis', 'Rodriguez', 'Martinez', 'Hernandez', 'Lopez', 'Gonzalez', 'Wilson', 'Anderson', 'Taylor'];
        const companies = ['Manhattan Property Management', 'NYC Building Services', 'Empire Property Group', 'Metro Facilities LLC', 'Brooklyn Property Solutions', 'Queens Management Co.', 'Bronx Building Services'];
        const titles = ['Property Manager', 'Facilities Manager', 'Building Superintendent', 'Operations Manager', 'Asset Manager'];
        
        // Use index to ensure consistent data for the same building
        const firstNameIndex = index % firstNames.length;
        const lastNameIndex = (index * 3) % lastNames.length;
        const companyIndex = (index * 5) % companies.length;
        const titleIndex = (index * 7) % titles.length;
        
        const firstName = firstNames[firstNameIndex];
        const lastName = lastNames[lastNameIndex];
        const company = companies[companyIndex];
        const title = titles[titleIndex];
        
        // Generate consistent phone and email based on index
        const phoneBase = 2120000000 + (index % 9999999);
        const phone = `(${phoneBase.toString().slice(3, 6)}) ${phoneBase.toString().slice(6, 9)}-${phoneBase.toString().slice(9, 13)}`;
        const email = `${firstName.toLowerCase()}.${lastName.toLowerCase()}@${company.toLowerCase().replace(/[^a-z]/g, '')}.com`;
        
        return {
            name: `${firstName} ${lastName}`,
            company,
            phone,
            email,
            title
        };
    }
    
    static calculateIRR(initialCost: number, annualSavings: number, years: number): number {
        // Simplified IRR calculation
        if (annualSavings <= 0) return 0;
        
        let rate = 0.1; // Start with 10%
        let tolerance = 0.0001;
        let maxIterations = 100;
        
        for (let i = 0; i < maxIterations; i++) {
            let npv = -initialCost;
            let npvDerivative = 0;
            
            for (let year = 1; year <= years; year++) {
                npv += annualSavings / Math.pow(1 + rate, year);
                npvDerivative -= year * annualSavings / Math.pow(1 + rate, year + 1);
            }
            
            if (Math.abs(npv) < tolerance) break;
            
            rate = rate - npv / npvDerivative;
        }
        
        return rate;
    }
    
    static filterViableProspects(buildings: Building[]): Building[] {
        return buildings.filter(building => 
            building.costBenefitAnalysis?.isViableProspect &&
            building.retrofitPotential?.paybackPeriod <= 15 &&
            building.costBenefitAnalysis?.totalSavingsOverLifetime > 0
        );
    }
    
    static sortByLL97SavingsPotential(buildings: Building[]): Building[] {
        return [...buildings].sort((a, b) => {
            const aSavings = a.costBenefitAnalysis?.totalSavingsOverLifetime || 0;
            const bSavings = b.costBenefitAnalysis?.totalSavingsOverLifetime || 0;
            return bSavings - aSavings;
        });
    }
    
    static generateSummaryStats(buildings: Building[]) {
        console.log('Generating summary stats for', buildings.length, 'buildings');
        
        const viableProspects = this.filterViableProspects(buildings);
        console.log('Viable prospects:', viableProspects.length);
        
        const totalLL97Fines = buildings.reduce((sum, b) => sum + (b.currentLL97Fee || 0), 0);
        const totalRetrofitCosts = viableProspects.reduce((sum, b) => sum + (b.retrofitPotential?.retrofitCost || 0), 0);
        const totalPotentialSavings = viableProspects.reduce((sum, b) => sum + (b.costBenefitAnalysis?.totalSavingsOverLifetime || 0), 0);
        
        // Calculate LL97 cap and gap statistics
        let totalCurrentEmissions = 0;
        let totalAllowedEmissions = 0;
        let totalGapAmount = 0;
        let buildingsOverLimit = 0;
        
        buildings.forEach(building => {
            if (building.totalGHGEmissions && building.grossFloorArea && building.buildingType) {
                const ll97LimitPerSqFt = LL97Service.getEmissionLimit(building.buildingType, 2025);
                const currentEmissions = building.totalGHGEmissions;
                const allowedEmissions = ll97LimitPerSqFt * building.grossFloorArea;
                const gapAmount = Math.max(0, currentEmissions - allowedEmissions);
                
                totalCurrentEmissions += currentEmissions;
                totalAllowedEmissions += allowedEmissions;
                totalGapAmount += gapAmount;
                
                if (gapAmount > 0) {
                    buildingsOverLimit++;
                }
            }
        });
        
        const stats = {
            totalBuildings: buildings.length,
            viableProspects: viableProspects.length,
            viabilityRate: (viableProspects.length / buildings.length) * 100,
            totalLL97Fines,
            totalRetrofitCosts,
            totalPotentialSavings,
            netBenefit: totalPotentialSavings - totalRetrofitCosts,
            averagePaybackPeriod: viableProspects.length > 0 ? viableProspects.reduce((sum, b) => sum + (b.retrofitPotential?.paybackPeriod || 0), 0) / viableProspects.length : 0,
            // LL97 Cap and Gap Statistics
            totalCurrentEmissions,
            totalAllowedEmissions,
            totalGapAmount,
            buildingsOverLimit,
            complianceRate: ((buildings.length - buildingsOverLimit) / buildings.length) * 100
        };
        
        console.log('Generated stats:', stats);
        return stats;
    }
}